import { MP4 } from './mp4'
import { concatUint8Array } from '../utils'

/**
 * @typedef {Object} RemuxResult
 * @property {Uint8Array} [videoInitSegment]
 * @property {Uint8Array} [audioInitSegment]
 * @property {Uint8Array} [videoSegment]
 * @property {Uint8Array} [audioSegment]
 */

export class FMP4Remuxer {
  /**
   * @param {import('../model').VideoTrack} videoTrack
   * @param {import('../model').AudioTrack} audioTrack
   */
  constructor (videoTrack, audioTrack) {
    this.videoTrack = videoTrack
    this.audioTrack = audioTrack
  }

  /**
   * @param {boolean} [createInit=false]
   * @returns {RemuxResult}
   */
  remux (createInit = false) {
    const videoTrack = this.videoTrack
    const audioTrack = this.audioTrack
    const hasVideo = videoTrack.exist()
    const hasAudio = audioTrack.exist()

    videoTrack.duration = videoTrack.duration || 0xffffffff
    audioTrack.duration = audioTrack.duration || 0xffffffff

    let videoInitSegment
    let audioInitSegment
    if (createInit) {
      if (hasVideo) videoInitSegment = MP4.initSegment([this.videoTrack])
      if (hasAudio) audioInitSegment = MP4.initSegment([this.audioTrack])
    }

    let videoSegment
    let audioSegment
    if (hasVideo && videoTrack.hasSample()) videoSegment = this._remuxVideo()
    if (hasAudio && audioTrack.hasSample()) audioSegment = this._remuxAudio()

    videoTrack.samples = []
    audioTrack.samples = []

    return {
      videoInitSegment,
      audioInitSegment,
      videoSegment,
      audioSegment
    }
  }

  _remuxVideo () {
    const track = this.videoTrack
    const samples = track.samples

    let mdatSize = 0

    samples.forEach((s) => {
      mdatSize += s.units.reduce((t, c) => (t + c.byteLength), 0)
      mdatSize += (s.units.length * 4)
    })

    const mdatData = new Uint8Array(mdatSize)
    const mdatView = new DataView(mdatData.buffer)

    for (let i = 0, l = samples.length, offset = 0, sample; i < l; i++) {
      sample = samples[i]

      let sampleSize = 0
      sample.units.forEach((u) => {
        mdatView.setUint32(offset, u.byteLength)
        offset += 4
        mdatData.set(u, offset)
        offset += u.byteLength
        sampleSize += (4 + u.byteLength)
      })
      sample.size = sampleSize
    }

    const moof = MP4.moof(this.videoTrack.sequenceNumber++, [track])
    const mdat = MP4.mdat(mdatData)

    return concatUint8Array(moof, mdat)
  }

  _remuxAudio () {
    const track = this.audioTrack
    const moof = MP4.moof(this.audioTrack.sequenceNumber++, [track])
    const mdatData = new Uint8Array(track.samples.reduce((t, c) => (t + c.size), 0))
    track.samples.reduce((offset, s) => {
      mdatData.set(s.data, offset)
      return offset + s.size
    }, 0)
    const mdat = MP4.mdat(mdatData)

    return concatUint8Array(moof, mdat)
  }
}
